// Dedupe classifier — pure data-in/data-out, no DB calls.
// Caller supplies the full set of existing clients (already normalized via
// useImportClients.findDuplicates) and one incoming row; we return the
// strongest match, if any.
//
// Strengths:
//   Strong — normalized phone identical, phone non-empty on both sides
//   Medium — normalized email identical, email non-empty on both sides,
//            phone missing or different
//   Weak   — normalized name exact match, no phone/email overlap
//   None   — no match

import type { DuplicateMatch, ExistingClient, NormalizedRow } from './types';

export function classifyMatch(
  incoming: NormalizedRow,
  existing: ExistingClient[],
): DuplicateMatch | null {
  if (existing.length === 0) return null;

  // Strong: phone match
  if (incoming.phone) {
    const phoneHit = existing.find(
      e => e.phone && e.phone === incoming.phone,
    );
    if (phoneHit) return toMatch(incoming, phoneHit, 'strong');
  }

  // Medium: email match (only when no strong)
  if (incoming.email) {
    const emailHit = existing.find(
      e => e.email && e.email === incoming.email,
    );
    if (emailHit) {
      const phoneConflict =
        incoming.phone && emailHit.phone && emailHit.phone !== incoming.phone;
      // Phone missing on either side, or phones differ — still medium.
      // (A phone match would already have short-circuited to 'strong' above.)
      if (!phoneConflict || !emailHit.phone || !incoming.phone) {
        return toMatch(incoming, emailHit, 'medium');
      }
      return toMatch(incoming, emailHit, 'medium');
    }
  }

  // Weak: name-only match (no phone/email overlap)
  const incomingNameLc = incoming.name.toLowerCase();
  if (incomingNameLc) {
    const nameHit = existing.find(
      e => e.name.toLowerCase() === incomingNameLc,
    );
    if (nameHit) return toMatch(incoming, nameHit, 'weak');
  }

  return null;
}

function toMatch(
  incoming: NormalizedRow,
  existing: ExistingClient,
  strength: 'strong' | 'medium' | 'weak',
): DuplicateMatch {
  return {
    incoming,
    existingClientId: existing.id,
    existingClientName: existing.name,
    existingClientPhone: existing.phone,
    existingClientEmail: existing.email,
    strength,
  };
}

export function defaultDecisionFor(strength: 'strong' | 'medium' | 'weak'):
  | 'skip'
  | 'merge'
  | 'import_new' {
  switch (strength) {
    case 'strong':
      return 'merge';
    case 'medium':
      return 'skip';
    case 'weak':
      return 'import_new';
  }
}
